import * as amplitude from "@amplitude/analytics-browser";
import { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { ExchangeRecommendations } from "@redotech/redo-model/agents/returns-agents/exchange-recommendations";
import { returnReasonsResponsesToText } from "@redotech/redo-model/agents/returns-agents/return-reasons";
import { SuggestedVariantOptions } from "@redotech/redo-model/agents/returns-agents/suggested-variant-options/suggested-variant-options";
import { DraftReturn } from "@redotech/redo-model/draft-return/draft-return";
import {
  ReturnableItem,
  ReturnProduct,
} from "@redotech/redo-model/draft-return/draft-return-items";
import { Provider } from "@redotech/redo-model/order";
import { ReturnFlow, Step } from "@redotech/redo-model/return-flow";
import { isAiExchangeEnabled } from "@redotech/redo-model/return-flow/ai-exchange-utils";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow/return-option";
import { SuggestedExchangeEventType } from "@redotech/redo-model/return-flow/suggested-exchange-events";
import { parseNumericIdFromShopifyId } from "@redotech/util/shopify-id";
import { RecommendedExchangeProductData } from "../hooks/use-recommended-exchange/use-recommended-exchange";
import { ReturnAppSettings } from "../settings";
import { shopifyGraphQLProductToProductData } from "../util";
import {
  getReturnStepPageOptions,
  isVariantInStock,
  ItemReturnFlowStep,
  ReturnOptionType,
} from "./util";

export async function getSuggestedVariantExchangeOptions({
  flow,
  flowStep,
  step,
  client,
  product,
  settings,
  returnItem,
  draftReturn,
  overrideIsAiExchangeEnabled = false,
}: {
  flow: ReturnFlow;
  flowStep: Step.Return | undefined;
  step: ItemReturnFlowStep | undefined;
  client: CustomerPortalRpcClient | undefined;
  product: ReturnProduct | undefined;
  settings: ReturnAppSettings | undefined;
  returnItem: ReturnableItem | undefined;
  draftReturn: DraftReturn | undefined;
  overrideIsAiExchangeEnabled?: boolean;
}): Promise<SuggestedVariantOptions> {
  const defaultSuggestedVariantExchangeOptions = {
    shouldSuggest: false,
    variantSuggestion: "",
    variantSuggestedResolution: "",
    suggestedOptions: [],
    sameItemReplacement: false,
  };

  try {
    // Are we on a return step that allows AI exchange suggestions?
    if (
      !overrideIsAiExchangeEnabled &&
      !isAiExchangeEnabled(flowStep, settings?.returnsAiExchange)
    ) {
      return defaultSuggestedVariantExchangeOptions;
    }

    // Do we have all the data we need?
    if (!client || !step || !product || !settings || !returnItem || !flowStep) {
      return defaultSuggestedVariantExchangeOptions;
    }

    // Is there a variant exchange option available?
    const hasVariantExchangeAvailable = getReturnStepPageOptions(
      flowStep,
      product,
      settings,
      returnItem,
      draftReturn?.returnType,
    ).some((option) => option.type === ReturnOptionType.VARIANT_EXCHANGE);
    if (!hasVariantExchangeAvailable) {
      return defaultSuggestedVariantExchangeOptions;
    }

    // Is there any variants in stock to exchange for?
    const variantsToChooseFrom = product.variantData.variants.filter(
      (variant) =>
        isVariantInStock({
          settings,
          returnType: draftReturn?.returnType,
          inventoryQuantity: variant.inventoryQuantity,
          inventoryPolicy: variant.inventoryPolicy,
          inventoryTracked: variant.inventoryTracked,
        }),
    );
    if (variantsToChooseFrom.length === 0) {
      return defaultSuggestedVariantExchangeOptions;
    }

    const reasons = returnReasonsResponsesToText(
      step.multipleChoiceAnswers,
      step.inputAnswers,
      flow,
    );

    const suggestedVariantExchangeOptions =
      await client.getSuggestedVariantOptions(
        {
          draftReturnId: draftReturn?._id ?? "",
          product,
          availableVariants: variantsToChooseFrom,
          reasons,
          aiReturnReasonsThreadId: step?.aiReturnReasonsThreadId,
        },
        { timeout: { seconds: 7 } },
      );

    amplitude.logEvent("suggested-variant-options", {
      shouldSuggest: suggestedVariantExchangeOptions.shouldSuggest,
    });

    return suggestedVariantExchangeOptions;
  } catch (error) {
    console.error("Error getting suggested variant exchange options", error);
    return defaultSuggestedVariantExchangeOptions;
  }
}

export async function getExchangeRecommendations({
  flow,
  flowStep,
  step,
  draftReturn,
  client,
  product,
  returnItem,
  settings,
  overrideIsAiExchangeEnabled = false,
}: {
  flow: ReturnFlow;
  flowStep: Step.Return | undefined;
  step: ItemReturnFlowStep | undefined;
  draftReturn: DraftReturn | undefined;
  client: CustomerPortalRpcClient | undefined;
  product: ReturnProduct | undefined;
  returnItem: ReturnableItem | undefined;
  settings: ReturnAppSettings | undefined;
  overrideIsAiExchangeEnabled?: boolean;
}): Promise<RecommendedExchangeProductData[]> {
  const defaultExchangeRecommendations: RecommendedExchangeProductData[] = [];

  try {
    // Are we on a return step that allows AI exchange suggestions?
    if (
      (!overrideIsAiExchangeEnabled &&
        !isAiExchangeEnabled(flowStep, settings?.returnsAiExchange)) ||
      flowStep?.optOutAiProductRecommendations ||
      // We currently only support exchange recommendations for Shopify products.
      !draftReturn?.orders.every((order) => order.platform === Provider.SHOPIFY)
    ) {
      return defaultExchangeRecommendations;
    }

    // Do we have all the data we need?
    if (!client || !step || !product || !returnItem || !settings || !flowStep) {
      return defaultExchangeRecommendations;
    }

    const returnOptions = getReturnStepPageOptions(
      flowStep,
      product,
      settings,
      returnItem,
      draftReturn?.returnType,
    );

    const exchangeReturnOption = returnOptions
      .find((option) => option.type === ReturnOptionType.SHOP_OR_RETURN)
      ?.options.find(
        (option) => option.method.type === ReturnOptionMethod.EXCHANGE,
      );

    const hasAdvancedExchange =
      exchangeReturnOption?.exchangeTypes.advanced ?? false;

    // Are there non-variant/same-item exchange options available?
    if (!hasAdvancedExchange) {
      return defaultExchangeRecommendations;
    }

    const reasons = returnReasonsResponsesToText(
      step.multipleChoiceAnswers,
      step.inputAnswers,
      flow,
    );

    const exchangeRecommendations: ExchangeRecommendations =
      await client.getExchangeRecommendations(
        {
          provider: Provider.SHOPIFY,
          returnedProduct: product,
          reasons,
          aiReturnReasonsThreadId: step?.aiReturnReasonsThreadId,
          draftReturnId: draftReturn?._id ?? "",
        },
        // Allowing more time than usual as we start it early and show variant first
        { timeout: { seconds: 12 } },
      );

    const excludedProductTags = new Set(
      settings?.exchanges?.excludeProductTags.map((tag) => tag.toLowerCase()),
    );

    const productData = exchangeRecommendations
      .filter((recommendedItem) => {
        const shopifyProduct = recommendedItem.product;

        // Is the product non-draft?
        if (shopifyProduct.publishedAt === null) {
          return false;
        }

        // Does the product have a tag for exclusion?
        if (
          shopifyProduct.tags.some((tag) =>
            excludedProductTags.has(tag.toLowerCase()),
          )
        ) {
          return false;
        }

        // Is not the product being returned
        if (
          parseNumericIdFromShopifyId(shopifyProduct.id) === product.productId
        ) {
          return false;
        }

        return true;
      })
      .map((recommendedItem) => {
        const recommendedProductData = shopifyGraphQLProductToProductData(
          recommendedItem.product,
          settings,
          draftReturn?.returnType,
        );
        return {
          product: recommendedProductData,
          recommendationMessage: recommendedItem.recommendationMessage || "",
        };
      })
      .filter((recommendedItem) =>
        recommendedItem.product.variants.some((variant) => variant.isInStock),
      );

    return productData;
  } catch (error) {
    console.error("Error getting exchange recommendations", error);
    return defaultExchangeRecommendations;
  }
}

export async function storeVariantExchangeRecommendations({
  client,
  draftReturn,
  returnableItem,
  product,
  suggestedVariantOptions,
  availableVariants,
}: {
  client: CustomerPortalRpcClient;
  draftReturn: DraftReturn;
  returnableItem: ReturnableItem;
  product: ReturnProduct;
  suggestedVariantOptions: SuggestedVariantOptions;
  availableVariants: Array<{
    id: string;
    title: string;
    selectedOptions: Array<{ name: string; value: string }>;
  }>;
}): Promise<void> {
  if (!suggestedVariantOptions.shouldSuggest || !client) {
    return;
  }

  try {
    const recommendations: Array<{
      productId: string;
      variantId: string;
      productTitle: string;
      variantTitle: string;
      rank: number;
      explanation: string;
    }> = [];

    let rank = 1;
    for (const suggestedOption of suggestedVariantOptions.suggestedOptions) {
      for (const suggestedValue of suggestedOption.values) {
        const matchingVariants = availableVariants.filter((variant) =>
          variant.selectedOptions.some(
            (option) =>
              option.name === suggestedOption.name &&
              option.value === suggestedValue,
          ),
        );

        for (const matchingVariant of matchingVariants) {
          recommendations.push({
            productId: product.productId,
            variantId: matchingVariant.id,
            productTitle: product.title,
            variantTitle: matchingVariant.title,
            rank: rank++,
            explanation:
              suggestedVariantOptions.variantSuggestedResolution ||
              `Recommended ${suggestedOption.name}: ${suggestedValue}`,
          });
        }
      }
    }

    await client.updateAiExchangeRecommendations({
      draftReturnId: draftReturn._id,
      returnableItemId: returnableItem.id,
      productId: product.productId,
      recommendationType: SuggestedExchangeEventType.VARIANT_EXCHANGE,
      recommendations,
    });

    console.log("Successfully stored variant exchange recommendations", {
      draftReturnId: draftReturn._id,
      returnableItemId: returnableItem.id,
      productId: product.productId,
      recommendationCount: recommendations.length,
    });
  } catch (error) {
    console.error("Error storing variant exchange recommendations", error);
  }
}

export async function storeAdvancedExchangeRecommendations({
  client,
  draftReturn,
  returnableItem,
  product,
  exchangeRecommendations,
}: {
  client: CustomerPortalRpcClient;
  draftReturn: DraftReturn;
  returnableItem: ReturnableItem;
  product: ReturnProduct;
  exchangeRecommendations: RecommendedExchangeProductData[];
}): Promise<void> {
  if (!exchangeRecommendations.length || !client) {
    return;
  }

  try {
    const recommendations = exchangeRecommendations.map((item, index) => ({
      productId: item.product.productId,

      variantId: item.product.variants[0]?.variantId || "", // update when we give a specific variant recommendation
      productTitle: item.product.productTitle,
      variantTitle: item.product.variants[0]?.variantTitle || "", // update when we give a specific variant recommendation
      rank: index + 1,
      explanation: item.recommendationMessage,
    }));

    await client.updateAiExchangeRecommendations({
      draftReturnId: draftReturn._id,
      returnableItemId: returnableItem.id,
      productId: product.productId,
      recommendationType: SuggestedExchangeEventType.RECOMMENDED_EXCHANGE,
      recommendations,
    });

    console.log("Successfully stored advanced exchange recommendations", {
      draftReturnId: draftReturn._id,
      returnableItemId: returnableItem.id,
      productId: product.productId,
      recommendationCount: recommendations.length,
    });
  } catch (error) {
    console.error("Error storing advanced exchange recommendations", error);
  }
}
